/**
 * List of all available services offered by Skinsio.
 * Used for dropdowns, filters, and display cards.
 */
export const SERVICE_NAMES = [
  "Carbon Laser Facial",
  "Full Body Laser Hair Reduction",
  "Laser Photo Facial",
  "PRP Facial",
  "Bridal Skincare",
  "Acne Treatment",
  "Scar Treatment",
  "Pigmentations",
  "Vampire Facial",
  "RF Skin Tightening",
  "Hair Transplants",
  "Physiotherapy",
  "Mesotherapy",
  "Dermal Fillers",
  "Botox",
  "Anti Aging Treatment",
  "Glutathione Therapy",
  "IV infusions",
  "Tattoo Removal",
  "Non Surgical Fat Reduction",
  "Rhinoplasty",
  "All Dental Subspecialties",
  "Skin Boosters/Stem Cells",
  "Medi - HydraFacial",
  "None/Not listed",
] as const;

export type ServiceName = (typeof SERVICE_NAMES)[number];

/**
 * Booking status options with associated UI styles and labels.
 */
export const STATUS_OPTIONS = [
  {
    label: "Pending",
    value: "pending",
    color: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400",
    iconColor: "text-yellow-500",
  },
  {
    label: "Confirmed",
    value: "confirmed",
    color: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    iconColor: "text-blue-500",
  },
  {
    label: "Completed",
    value: "completed",
    color: "bg-green-500/10 text-green-600 dark:text-green-400",
    iconColor: "text-green-500",
  },
  {
    label: "Cancelled",
    value: "cancelled",
    color: "bg-red-500/10 text-red-600 dark:text-red-400",
    iconColor: "text-red-500",
  },
] as const;

export type BookingStatus = (typeof STATUS_OPTIONS)[number]["value"];

/**
 * Booking source types.
 */
export const BOOKING_SOURCES = {
  WEBSITE: "website",
  MANUAL: "manual",
} as const;

export type BookingSource =
  (typeof BOOKING_SOURCES)[keyof typeof BOOKING_SOURCES];

export const DEFAULT_SERVICE = "Consultation";

/**
 * Predefined time ranges for dashboard filtering.
 */
export const TIME_RANGE_OPTIONS = [
  { label: "All Time", value: "all" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "week" },
  { label: "Last 14 Days", value: "two-weeks" },
  { label: "Last 30 Days", value: "month" },
] as const;

export type TimeRange = (typeof TIME_RANGE_OPTIONS)[number]["value"];

/**
 * Generates available time slots for appointments based on the day of the week.
 * Mon-Sat: 10:30 AM to 7:00 PM (Last slot 6:45 PM)
 * Sun: 10:30 AM to 3:00 PM (Last slot 2:45 PM)
 *
 * If the given date is today, slots that have already passed (plus a 15
 * minute buffer so people can't book something starting in 2 minutes) are
 * excluded from the list.
 */
export const getTimeSlotsForDate = (date?: Date) => {
  const slots: string[] = [];
  const startHour = 10;
  const startMinute = 30;

  // Default end time (Mon-Sat)
  let endHour = 19;
  let endMinute = 0;

  // Sunday end time
  if (date && date.getDay() === 0) {
    endHour = 15;
    endMinute = 0;
  }

  let currentHour = startHour;
  let currentMinute = startMinute;

  // If booking for today, hide slots that have already passed (with a
  // buffer so the very next slot isn't bookable with zero notice).
  const now = new Date();
  const isToday =
    !!date &&
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const bufferMinutes = 15;
  const cutoffTotalMinutes = isToday
    ? now.getHours() * 60 + now.getMinutes() + bufferMinutes
    : -1;

  while (
    currentHour < endHour ||
    (currentHour === endHour && currentMinute < endMinute)
  ) {
    const slotTotalMinutes = currentHour * 60 + currentMinute;

    if (!isToday || slotTotalMinutes >= cutoffTotalMinutes) {
      const hour12 =
        currentHour > 12
          ? currentHour - 12
          : currentHour === 0
            ? 12
            : currentHour;
      const period = currentHour >= 12 ? "PM" : "AM";
      const minuteStr = currentMinute.toString().padStart(2, "0");
      slots.push(`${hour12}:${minuteStr} ${period}`);
    }

    currentMinute += 15;
    if (currentMinute >= 60) {
      currentMinute = 0;
      currentHour += 1;
    }
  }
  return slots;
};

// Default slots for initial rendering or when no date is selected (uses Mon-Sat hours)
export const TIME_SLOTS = getTimeSlotsForDate();

/**
 * Age options for booking form (18 to 100).
 */
export const AGE_OPTIONS = Array.from({ length: 83 }, (_, i) => String(i + 18));

/**
 * Gender options for booking form.
 */
export const GENDER_OPTIONS = ["Male", "Female", "Other"] as const;

import {
  Sparkles,
  Scissors,
  Sun,
  Droplets,
  Heart,
  Zap,
  Leaf,
  Activity,
  Star,
  Palette,
  Waves,
} from "lucide-react";

/**
 * Detailed information for each service, including icons and descriptions.
 * Used for the Services section on the landing page.
 */
export const SERVICES_DATA = [
  {
    icon: Heart,
    title: "Bridal Skincare",
    description:
      "Customized regimen for glowing, flawless skin on your special day.",
  },
  {
    icon: Leaf,
    title: "Acne Treatment",
    description:
      "Targeted solutions to clear skin and reduce breakouts effectively today.",
  },
  {
    icon: Star,
    title: "Scar Treatment",
    description:
      "Specialized treatments to diminish scars and boost your confidence now.",
  },
  {
    icon: Palette,
    title: "Pigmentations",
    description: "Brighten and even out your skin tone with safe procedures.",
  },
  {
    icon: Sun,
    title: "Carbon Laser Facial",
    description:
      "Non-invasive treatment to refresh skin, reducing aging and uneven tone.",
  },
  {
    icon: Sparkles,
    title: "Full Body Laser Hair Reduction",
    description:
      "Advanced laser technology for smooth, hair-free skin with lasting results.",
  },
  {
    icon: Zap,
    title: "Laser Photo Facial",
    description:
      "Stimulates collagen for firmer, tighter skin with radiant rejuvenation results.",
  },
  {
    icon: Droplets,
    title: "PRP Facial",
    description:
      "Harness your body's natural healing power for youthful skin revitalization.",
  },
  {
    icon: Droplets,
    title: "Vampire Facial",
    description:
      "Innovative PRP treatment for natural skin renewal and youthful radiance.",
  },
  {
    icon: Waves,
    title: "RF Skin Tightening",
    description:
      "Radiofrequency technology for firmer, lifted skin without any surgical procedures.",
  },
  {
    icon: Scissors,
    title: "Hair Transplants",
    description:
      "Natural hair restoration with personalized solutions for lasting, visible results.",
  },
  {
    icon: Activity,
    title: "Physiotherapy",
    description:
      "Healing through movement for improved mobility, strength, and overall well-being.",
  },
  {
    icon: Droplets,
    title: "Mesotherapy",
    description:
      "Micro-injections of vitamins for deep skin rejuvenation and hair growth.",
  },
  {
    icon: Sparkles,
    title: "Dermal Fillers",
    description:
      "Injectable treatments to restore volume, smooth lines, and enhance contours.",
  },
  {
    icon: Zap,
    title: "Botox",
    description:
      "Safe, effective treatment to reduce fine lines and wrinkles instantly.",
  },
  {
    icon: Star,
    title: "Anti Aging Treatment",
    description:
      "Comprehensive solutions to combat signs of aging and maintain youth.",
  },
  {
    icon: Heart,
    title: "Glutathione Therapy",
    description:
      "Powerful antioxidant treatment for skin brightening and overall body wellness.",
  },
  {
    icon: Activity,
    title: "IV infusions",
    description:
      "Nutrient-rich intravenous therapy for hydration, immunity, and glowing skin health.",
  },
  {
    icon: Scissors,
    title: "Tattoo Removal",
    description:
      "Advanced laser technology that safely and effectively removes unwanted tattoos.",
  },
  {
    icon: Activity,
    title: "Non Surgical Fat Reduction",
    description:
      "Advanced technology to contour body and reduce stubborn fat safely.",
  },
  {
    icon: Sparkles,
    title: "Rhinoplasty",
    description:
      "Expert nose reshaping procedures to enhance facial harmony and confidence.",
  },
  {
    icon: Activity,
    title: "All Dental Subspecialties",
    description:
      "Comprehensive dental care: implants, smile design, and painless root canals.",
  },
  {
    icon: Sparkles,
    title: "Skin Boosters/Stem Cells",
    description:
      "Bio-remodelling with hyaluronic acid for deep repair and volume.",
  },
  {
    icon: Droplets,
    title: "Medi - HydraFacial",
    description: "Premium 6-step facial for deep cleansing and instant glow.",
  },
];
